import React, { useEffect } from "react";
import styles from "./css/Form.module.css";
import { useForm } from "../../Utils/FormContext";
import FormGroup from "./FormGroup";
import taqeForm from "../../Utils/taqeForm";
import taqeAppIntegration from "../../Utils/taqeAppIntegration";
import { useGetCity, useGetState } from "../../Hooks/useStateCity";
import useFetch from "../../Hooks/useFetch";
import { FORM_CREATE_USER_POST } from "../../api";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import Modal from "../Modal";
import FormGroupMult from "./FormGroupMult";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const Form: React.FC = () => {
  const {
    form,
    setForm,
    blockSend,
    utmParams,
    setAnswered,
    setRefusal,
    setBlockSend,
    setJsonResponse,
  } = useForm();
  const [checkbox, setCheckbox] = React.useState(false);
  const [submitData, setSubmitData] = React.useState(false);
  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const { request } = useFetch();

  useEffect(() => {
    if (!form.cnh || !form.deficiencia) {
      setBlockSend(true);
      return;
    }

    if (form.cnh.length === 0 || form.deficiencia.length === 0) {
      setBlockSend(true);
    } else {
      setBlockSend(false);
    }

    if (!checkbox) setBlockSend(true);
    else setBlockSend(false);
  }, [form, setBlockSend, checkbox]);

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [event.target.id]: event.target.value }));
  };

  const handleChangeSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    let newValue: any = event.target.value;

    setForm((prev) => ({ ...prev, [event.target.id]: newValue }));
  };

  const isDateAgeorMoreYearsOld = (date: Date, age: number) => {
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    return new Date(year + age, month, day) <= new Date();
  };

  const handleSubmitRefusal = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    await addDoc(collection(db, "forms"), {
      ...{ ...form, telefone: form.telefone.replace(/\D/g, "") },
      utm: utmParams,
    });

    setRefusal(true);
    return;
  };

  const isNotAllowed: () => boolean = () => {
    if (
      isDateAgeorMoreYearsOld(new Date(form.data_nascimento), 30) ||
      !isDateAgeorMoreYearsOld(new Date(form.data_nascimento), 16)
    ) {
      return true;
    }

    if (
      form.escolaridade === "Ensino Fundamental Cursando" ||
      form.escolaridade === "Ensino Fundamental Completo"
    ) {
      return true;
    }

    return false;
  };

  const sendForm = () => {
    const [year, month, day] = String(form.data_nascimento).split("-");

    return {
      lastName: form.nome,
      personEmail: form.email,
      personMobilePhone: form.telefone.replace(/\D/g, ""),
      cpf: form.cpf.replace(/\D/g, ""),
      personBirthdate: `${day}/${month}/${year}`,
      racaEtnia: form.raca,
      genderIdentify: form.genero,
      quaisDeficiencias: form.deficiencia?.filter(
        (element) => element !== "none"
      ),
      endereco: {
        personMailingStreet: form.rua,
        bairro: form.bairro,
        personMailingCity: form.cidade,
        personMailingStateCode: form.estado,
        personMailingPostalCode: form.cep,
      },
      nomeResponsavel: form.nome_responsavel ?? undefined,
      cpfResponsavel: form.cpf_responsavel
        ? form.cpf_responsavel.replace(/\D/g, "")
        : undefined,
      escolaridade: form.escolaridade,
      cnh: form.cnh?.filter((element) => element !== "none"),
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitData(true);

    const formData = sendForm();

    const { options, url } = FORM_CREATE_USER_POST(formData);

    const { response, json } = await request(url, options);

    setJsonResponse(json);

    if (json?.success) {
      setAnswered(true);
    }

    if (String(json?.details).trim() === "Jovem já está cadastrado.") {
      setRefusal(true);
    }

    await addDoc(collection(db, "forms"), {
      ...{ ...form, telefone: form.telefone.replace(/\D/g, "") },
      lys_response: {
        status: response?.status,
        response: json,
      },
      utm: utmParams,
      createdAt: serverTimestamp(),
    });

    if (
      json?.success &&
      String(json?.message).trim() === "User successfully registered"
    ) {
      try {
        window.dataLayer.push({
          event: "form_sucess_iccb",
          formLocation: "lp_iccb",
          email: formData.personEmail,
          telefone: formData.personMobilePhone,
          utm: utmParams,
        });
      } catch (err) {
        console.error("err", err);
      }
    }

    if (json?.link) {
      localStorage.removeItem("form");
      localStorage.removeItem("params");
      window.location.href = json.link;
    } else {
      setShowErrorMessage(true);
    }

    setSubmitData(false);
  };

  useEffect(() => {
    const handleMessage = (event: any) => {
      const validUrlsOrigins: any =
        process.env.REACT_APP_LIST_OF_VALID_SOURCE_URLS;

      if (validUrlsOrigins.includes(event.origin)) {
        const data: any = event.data;
        setForm((prev) => ({
          ...prev,
          ...taqeAppIntegration.loadUserIntegrationInfo(form, data),
        }));
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className={styles.background} id="form">
      <div className={styles.container}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            if (isNotAllowed()) handleSubmitRefusal(e);
            else handleSubmit(e);
          }}
        >
          {/* nome */}
          <FormGroup
            name={taqeForm.name.name}
            type="text"
            value={form[taqeForm.name.name] || ""}
            title={taqeForm.name.title}
            placeholder={taqeForm.name.placeholder}
            handleChangeInput={handleChangeInput}
            errorMessage={taqeForm.name.error}
            mask={taqeForm.name.mask}
            required
          />

          {/* telefone */}
          <FormGroup
            name={taqeForm.phone.name}
            type="text"
            value={form[taqeForm.phone.name] || ""}
            title={taqeForm.phone.title}
            placeholder={taqeForm.phone.placeholder}
            handleChangeInput={handleChangeInput}
            errorMessage={taqeForm.phone.error}
            mask={taqeForm.phone.mask}
            required
          />

          {/* email */}
          <FormGroup
            name={taqeForm.email.name}
            type="text"
            value={form[taqeForm.email.name] || ""}
            title={taqeForm.email.title}
            placeholder={taqeForm.email.placeholder}
            handleChangeInput={handleChangeInput}
            errorMessage={taqeForm.email.error}
            required
          />

          {/* data_de_nascimento */}
          <FormGroup
            name={taqeForm.birthday.name}
            type="date"
            value={form[taqeForm.birthday.name] || ""}
            title={taqeForm.birthday.title}
            placeholder={taqeForm.birthday.placeholder}
            handleChangeInput={handleChangeInput}
            errorMessage={taqeForm.birthday.error}
            mask={taqeForm.birthday.mask}
            required
          />

          {/* cpf */}
          <FormGroup
            name={taqeForm.cpf.name}
            type="text"
            value={form[taqeForm.cpf.name] || ""}
            title={taqeForm.cpf.title}
            placeholder={taqeForm.cpf.placeholder}
            handleChangeInput={handleChangeInput}
            errorMessage={taqeForm.cpf.error}
            mask={taqeForm.cpf.mask}
            required
          />

          <p className={styles.form_paragraph}>
            As duas perguntas a seguir referem-se à sua autodeclaração de
            gênero. Prezamos pela diversidade e inclusão de todas as pessoas,
            pois acreditamos que existem muitas formas de se expressar. Sinta-se
            livre para selecionar a opção com a qual mais se identifica. E
            lembre-se de que você também pode escolher não declarar escolhendo a
            última opção de resposta.
          </p>

          {/* genero */}
          <FormGroup
            name={taqeForm.gender.name}
            type="option"
            value={form[taqeForm.gender.name] || ""}
            title={taqeForm.gender.title}
            placeholder={taqeForm.gender.placeholder}
            handleChangeSelect={handleChangeSelect}
            options={taqeForm.gender.options}
            required
          />

          {/* raca */}
          <FormGroup
            name={taqeForm.race.name}
            type="option"
            value={form[taqeForm.race.name] || ""}
            title={taqeForm.race.title}
            placeholder={taqeForm.race.placeholder}
            handleChangeSelect={handleChangeSelect}
            options={taqeForm.race.options}
            required
          />

          {/* disability */}
          <FormGroupMult
            name={taqeForm.disability.name}
            value={form[taqeForm.disability.name] || []}
            title={taqeForm.disability.title}
            placeholder={taqeForm.disability.placeholder}
            options={taqeForm.disability.options || []}
            required={false}
          />

          {/* school */}
          <FormGroup
            name={taqeForm.school.name}
            type="option"
            value={form[taqeForm.school.name] || ""}
            title={taqeForm.school.title}
            placeholder={taqeForm.school.placeholder}
            handleChangeSelect={handleChangeSelect}
            options={taqeForm.school.options}
            required
          />

          {/* cnh */}
          <FormGroupMult
            name={taqeForm.cnh.name}
            value={form[taqeForm.cnh.name] || []}
            title={taqeForm.cnh.title}
            placeholder={taqeForm.cnh.placeholder}
            options={taqeForm.cnh.options || []}
            required={false}
          />

          {/* state */}
          <FormGroup
            name={taqeForm.state.name}
            type="option"
            value={form[taqeForm.state.name] || ""}
            title={taqeForm.state.title}
            placeholder={taqeForm.state.placeholder}
            handleChangeSelect={handleChangeSelect}
            options={useGetState()}
            required
          />

          {/* city */}
          <FormGroup
            name={taqeForm.city.name}
            type="option"
            value={form[taqeForm.city.name] || ""}
            title={taqeForm.city.title}
            placeholder={
              form[taqeForm.state.name]
                ? taqeForm.city.placeholder
                : "Selecione um estado primeiro"
            }
            handleChangeSelect={handleChangeSelect}
            options={useGetCity(
              form[taqeForm.state.name] ? String(form[taqeForm.state.name]) : ""
            )}
            disabled={form[taqeForm.state.name] ? false : true}
            required
          />

          {/* neighborhood */}
          <FormGroup
            name={taqeForm.neighborhood.name}
            type="text"
            value={form[taqeForm.neighborhood.name] || ""}
            title={taqeForm.neighborhood.title}
            placeholder={taqeForm.neighborhood.placeholder}
            handleChangeInput={handleChangeInput}
            required
          />

          {/* street */}
          <FormGroup
            name={taqeForm.street.name}
            type="text"
            value={form[taqeForm.street.name] || ""}
            title={taqeForm.street.title}
            placeholder={taqeForm.street.placeholder}
            handleChangeInput={handleChangeInput}
            required
          />

          {/* zipCode */}
          <FormGroup
            name={taqeForm.zipCode.name}
            type="text"
            value={form[taqeForm.zipCode.name] || ""}
            title={taqeForm.zipCode.title}
            placeholder={taqeForm.zipCode.placeholder}
            handleChangeInput={handleChangeInput}
            errorMessage={taqeForm.zipCode.error}
            mask={taqeForm.zipCode.mask}
            required
          />

          {/* responsible_name */}
          <FormGroup
            name={taqeForm.responsible_name.name}
            type="text"
            value={form[taqeForm.responsible_name.name] || ""}
            title={taqeForm.responsible_name.title}
            placeholder={taqeForm.responsible_name.placeholder}
            handleChangeInput={handleChangeInput}
            disabled={
              !form[taqeForm.birthday.name] ||
              isDateAgeorMoreYearsOld(
                new Date(
                  form[taqeForm.birthday.name]
                    ? String(form[taqeForm.birthday.name])
                    : ""
                ),
                18
              )
            }
            required={
              !isDateAgeorMoreYearsOld(
                new Date(
                  form[taqeForm.birthday.name]
                    ? String(form[taqeForm.birthday.name])
                    : ""
                ),
                18
              )
            }
          />

          {/* responsible_cpf */}
          <FormGroup
            name={taqeForm.responsible_cpf.name}
            type="text"
            value={form[taqeForm.responsible_cpf.name] || ""}
            title={taqeForm.responsible_cpf.title}
            placeholder={taqeForm.responsible_cpf.placeholder}
            handleChangeInput={handleChangeInput}
            disabled={
              !form[taqeForm.birthday.name] ||
              isDateAgeorMoreYearsOld(
                new Date(
                  form[taqeForm.birthday.name]
                    ? String(form[taqeForm.birthday.name])
                    : ""
                ),
                18
              )
            }
            mask={taqeForm.responsible_cpf.mask}
            required={
              !isDateAgeorMoreYearsOld(
                new Date(
                  form[taqeForm.birthday.name]
                    ? String(form[taqeForm.birthday.name])
                    : ""
                ),
                18
              )
            }
            errorMessage={taqeForm.responsible_cpf.error}
          />

          {/* knowing_by */}
          <FormGroup
            name={taqeForm.knowing_by.name}
            type="option"
            value={form[taqeForm.knowing_by.name] || ""}
            title={taqeForm.knowing_by.title}
            placeholder={taqeForm.knowing_by.placeholder}
            handleChangeSelect={handleChangeSelect}
            options={taqeForm.knowing_by.options}
            required
          />

          <label className={styles.term} htmlFor="terms">
            <input
              className={styles.term_checkbox}
              type="checkbox"
              id="terms"
              name="terms"
              checked={checkbox}
              onChange={() => {
                setCheckbox(!checkbox);
                // set date with time DD-MM-AAAA HH:MM:SS
                setForm((prev) => ({
                  ...prev,
                  data_termos: `${new Date().toLocaleDateString(
                    "pt-BR"
                  )} ${new Date().toLocaleTimeString("pt-BR")}`.replace(
                    /\//g,
                    "-"
                  ),
                }));
              }}
              required
            />
            <span className={styles.term_text}>
              Para participar do curso, é necessário ler e aceitar os{" "}
              <span onClick={() => setOpen(true)}>
                termos de uso e política de privacidade
              </span>{" "}
              do Instituto Coca-Cola Brasil. E também estar de acordo com o{" "}
              <a
                href="https://www.taqe.com.br/aviso-de-privacidade/"
                target="_blank"
                rel="noreferrer"
              >
                aviso de privacidade.
              </a>
            </span>
          </label>

          <button
            className={styles.form_button}
            type="submit"
            disabled={blockSend || submitData}
          >
            ENVIAR
          </button>

          {showErrorMessage && (
            <span className={styles.error_message}>
              Ops! Algo deu errado, tente novamente em instantes.
            </span>
          )}
          {blockSend && (
            <span className={styles.error_message}>
              Preencha todos os campos corretamente.
            </span>
          )}
        </form>
      </div>

      {open && <Modal setIsOpen={setOpen} />}
    </div>
  );
};

export default Form;
